import Image from "next/image";

import cn from "classnames";
import type { ReactNode } from "react";

import {
  ButtonLink,
  resolveCtaLink,
} from "@/components/buttonLink/buttonLink.component";
import { ArrowRightIcon } from "@/components/icons/arrowRight.icon";
import { CheckmarkThinIcon } from "@/components/icons/checkmarkThin.icon";
import { RichText } from "@/components/richText/richText.component";

import { brown } from "@/app/font";

import type { AdtTwoWaysOption } from "./adtTwoWays.component";

export const AdtTwoWaysCard = ({
  option,
  panelId,
  labelledBy,
  isSelected,
  ctaVariant,
}: AdtTwoWaysCardProps) => {
  const imageUrl = option.image?.url;

  const belowCtaDescription = option.belowCTAdescription;
  const hasBelowCtaDescription = Boolean(belowCtaDescription?.length);

  const renderCta = () => {
    const cta = resolveCtaLink(option);
    const hasCta = Boolean(cta && cta.href !== "#");

    if (!hasCta && !hasBelowCtaDescription) return null;

    return (
      <div className="mt-auto flex flex-col items-start gap-3 pt-2">
        {hasCta ? (
          <ButtonLink
            cta={option}
            className={cn(
              "w-full px-7! py-3.5! text-[18px]! normal-case! lg:w-auto",
              "focus-visible:ring-primary! focus-visible:ring-offset-blu-navy!",
              {
                "bg-primary! text-secondary! hover:bg-primary-400":
                  ctaVariant === "filled",
                "border-white! bg-transparent! text-white! hover:bg-white/10":
                  ctaVariant === "outline",
              },
            )}>
            {cta?.phone ? null : (
              <>
                {cta?.label}
                <ArrowRightIcon
                  aria-hidden
                  title=""
                  width={20}
                  height={20}
                  className="size-5"
                />
              </>
            )}
          </ButtonLink>
        ) : null}
        {hasBelowCtaDescription ? (
          <div
            data-testid={`adt-two-ways-below-cta-${option._key}`}
            className="text-white [&_.legal]:text-white [&_a]:underline [&_a]:underline-offset-2">
            <RichText>{belowCtaDescription}</RichText>
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <article
      id={panelId}
      role="tabpanel"
      aria-labelledby={labelledBy}
      data-testid={`adt-two-ways-card-${option._key}`}
      className={cn(
        "bg-blu-navy relative isolate flex min-h-0 flex-col overflow-hidden rounded-[20px] text-white",
        isSelected ? "flex" : "hidden",
        "lg:flex lg:min-h-125",
      )}>
      {imageUrl ? (
        <Image
          src={imageUrl}
          alt={option.image?.alt ?? ""}
          fill
          sizes="(min-width: 1024px) 560px, 100vw"
          unoptimized
          className="absolute inset-0 -z-20 hidden object-cover object-[68%_center] lg:block"
        />
      ) : null}
      <div
        aria-hidden
        className="bg-blu-navy/20 pointer-events-none absolute inset-0 -z-10 hidden lg:block"
      />
      <div
        aria-hidden
        className="from-blu-navy via-blu-navy/72 pointer-events-none absolute inset-0 -z-10 hidden bg-linear-to-r via-42% to-transparent lg:block"
      />
      <div className="relative z-10 flex flex-1 flex-col gap-5 px-6 py-8 sm:px-8 sm:py-10 lg:px-9 lg:py-10">
        {option.content?.length ? (
          <div className="[&_.adt-two-ways-check+.adt-two-ways-check]:mt-3">
            <RichText
              blockComponents={cardBlockComponents}
              listComponents={listComponents}
              listItemComponents={listItemComponents}>
              {option.content}
            </RichText>
          </div>
        ) : null}
        {renderCta()}
      </div>
    </article>
  );
};

type AdtTwoWaysCardProps = {
  option: AdtTwoWaysOption & { _key: string };
  panelId: string;
  labelledBy: string;
  isSelected: boolean;
  ctaVariant: "filled" | "outline";
};

const CheckmarkRow = ({ children }: { children?: ReactNode }) => (
  <div className="adt-two-ways-check flex items-start gap-2.5 text-[16px] leading-snug [&_p]:m-0">
    <CheckmarkThinIcon
      aria-hidden
      title=""
      width={20}
      height={20}
      className="text-primary mt-0.5 size-5 shrink-0"
    />
    <span>{children}</span>
  </div>
);

const cardBlockComponents = {
  preHeaderSm: ({ children }: { children?: ReactNode }) => (
    <p className="mb-3 text-[12px] font-bold tracking-[0.08em] text-white/90 uppercase sm:text-[13px]">
      {children}
    </p>
  ),
  h3: ({ children }: { children?: ReactNode }) => (
    <h3
      className={`${brown.className} mb-5 text-[32px] leading-[1.12] font-bold sm:text-[36px]`}>
      {children}
    </h3>
  ),
  featureThinCheck: ({
    children,
    value,
  }: {
    children?: ReactNode;
    value?: { listItem?: unknown };
  }) =>
    value?.listItem ? (
      <span>{children}</span>
    ) : (
      <CheckmarkRow>{children}</CheckmarkRow>
    ),
};

const listComponents = {
  bullet: ({ children }: { children?: ReactNode }) => (
    <ul className="flex list-none flex-col gap-3 p-0">{children}</ul>
  ),
};

const listItemComponents = {
  bullet: ({ children }: { children?: ReactNode }) => (
    <li>
      <CheckmarkRow>{children}</CheckmarkRow>
    </li>
  ),
};
